import NotificationService from './services/NotificationService';
import {rememberReturn,pendingReturn} from './navigation/nativeReturn';
import NativeNotificationBridge from './navigation/NativeAppBridge';
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import NativeFeatures from './services/NativeFeatures';
import { facebookPixel } from './services/FacebookPixelService';
import { googleAnalytics } from './services/GoogleAnalyticsService';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import NotFound from './pages/NotFound';
import PrivacyChoice from './components/PrivacyChoice';
import NativeAnalyticsBridge from './components/NativeAnalyticsBridge';
import RouteAlias from './navigation/RouteAlias';


const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/Login'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ActivityHome = lazy(() => import('./features/activity/ActivityHome'));
const CashPlanPage = lazy(() => import('./features/payday/CashPlanPage'));
const PlanHome = CashPlanPage;
const Goals = lazy(() => import('./pages/Goals'));
const CreditCards = lazy(() => import('./pages/CreditCards'));
const Categories = lazy(() => import('./features/activity/CategoryChoices'));
const AccountHome = lazy(() => import('./pages/AccountHome'));
const Checkout = lazy(() => import('./pages/Checkout'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const CheckoutCancel = lazy(() => import('./pages/CheckoutCancel'));
const RedirectHandler = lazy(() => import('./pages/RedirectHandler'));
const Admin = lazy(() => import('./pages/Admin'));

// Scroll Restoration Component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      // Try multiple scroll methods for better compatibility
      if (window.scrollTo) {
        window.scrollTo(0, 0);
      }
      
      // Also try scrolling the document element
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
      
      // And the body element
      if (document.body) {
        document.body.scrollTop = 0;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [pathname]);

  return null;
}

// Onboarding Route Component
function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, authLoading } = useAuth();

  // Wait for auth to resolve before deciding
  if (authLoading) {
    return <LoadingSpinner showBranding={!!currentUser} />;
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  return <>{children}</>;
}

// Protected Route Component for authenticated pages (requires auth + onboarding for new users)
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, userProfile, authLoading } = useAuth();
  const location=useLocation();
  useEffect(()=>{if(!authLoading&&(!currentUser||userProfile?.hasOnboarded!==true)&&!pendingReturn()&&location.pathname!=='/dashboard')rememberReturn(location.pathname+location.search);},[authLoading,currentUser,userProfile?.hasOnboarded,location.pathname,location.search]);

  // Wait for auth to resolve before redirecting
  if (authLoading) {
    return <LoadingSpinner showBranding={!!currentUser} />;
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  // Wait for profile so we know if user has onboarded (avoid flash of dashboard)
  if (userProfile === null) {
    return <LoadingSpinner showBranding />;
  }

  // New users: send to onboarding before any protected page (treat missing/undefined as not onboarded)
  if (userProfile && userProfile.hasOnboarded !== true) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner showBranding />}>
      <Routes>
        <Route path="/activity" element={<ProtectedRoute><Layout><ActivityHome /></Layout></ProtectedRoute>} />
        <Route path="/plan" element={<ProtectedRoute><Layout><PlanHome /></Layout></ProtectedRoute>} />
        <Route path="/expenses" element={<RouteAlias to="/activity/tracked" />} />
        <Route path="/goals" element={<RouteAlias to="/plan/goals" />} />
        <Route path="/credit-cards" element={<RouteAlias to="/plan/debt" />} />
        <Route path="/categories" element={<RouteAlias to="/activity/categories" />} />
        <Route path="/settings" element={<RouteAlias to="/account" />} />
        {/* Public Routes - Always accessible */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Navigate to="/login?mode=signup" replace />} />
        <Route path="/r/:token" element={<RedirectHandler />} />
        
        {/* Onboarding Route */}
        <Route 
          path="/onboarding"
          element={
            <OnboardingRoute>
              <Onboarding />
            </OnboardingRoute>
          } 
        />
        
        {/* Protected Routes - Require authentication and onboarding */}
        <Route path="/activity/cashflow" element={<RouteAlias to="/plan" />} />
        <Route path="/plan/cash" element={<RouteAlias to="/plan" />} />
        <Route path="/activity/legacy-cashflow" element={<RouteAlias to="/plan" />} />
        <Route path="/activity/bank" element={<RouteAlias to="/activity" />} />
        <Route 
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/activity/tracked"
          element={
            <RouteAlias to="/activity" />
          } 
        />
        <Route 
          path="/plan/goals"
          element={
            <ProtectedRoute>
              <Layout>
                <Goals />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/plan/debt"
          element={
            <ProtectedRoute>
              <Layout>
                <CreditCards />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/activity/categories"
          element={
            <ProtectedRoute>
              <Layout>
                <Categories />
              </Layout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/account"
          element={
            <ProtectedRoute>
              <Layout>
                <AccountHome />
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Checkout Routes - Require authentication but no layout */}
        <Route 
          path="/checkout"
          element={
            <ProtectedRoute>
              <Checkout />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/checkout/success"
          element={
            <ProtectedRoute>
              <CheckoutSuccess />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/checkout/cancel"
          element={
            <ProtectedRoute>
              <CheckoutCancel />
            </ProtectedRoute>
          } 
        />
        
        {/* Introduce the website; native clients retain their sign-in entry. */}
        <Route path="/" element={<Landing />} />
        
        {/* Admin Route - Must come before catch-all */}
        <Route path="/admin" element={<Admin />} />
        
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    // Initialize native features
    NativeFeatures.initialize();
    
    // Initialize Facebook Pixel
    facebookPixel.init();
    
    // Initialize Google Analytics
    googleAnalytics.init();
    
    // Initialize notification service asynchronously to prevent blocking app startup
    // This is especially important on Android where Firebase might not be configured
    setTimeout(() => {
      NotificationService.initialize().catch((error) => {
        console.error('Failed to initialize notifications:', error);
        // Don't crash the app if notifications fail
      });
    }, 500);
    
    }, []);

  return (
    <Router>
      <AuthProvider>
        <SettingsProvider>
          <div className="min-h-screen">
            <ScrollToTop />
            <NativeNotificationBridge />
            <NativeAnalyticsBridge />
            <AppRoutes />
            <PrivacyChoice />
          </div>
        </SettingsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
